import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem } from 'reactstrap';

import { Button, ClickableIcon, Spinner, Toolbar } from '~components';
import Icon, { faArrowLeft, faPlus } from '~components/icon';

import { CarePlan } from '~clients/models';

import ActivationChecklistPanel from '~care-plans/ActivationChecklistPanel';
import CarePlanListItem from '~care-plans/CarePlanListItem';

interface ComponentProps {
  clientName: string;
  plans: CarePlan[];
  selectedPlanId?: string;
  selectedPlan?: CarePlan;
  onSelectPlan: (planId: string) => void;
  getPackageName: (extPackageId: number) => string | undefined;
  isLoading: boolean;
  onNewCarePlan: () => void;
}

const PLANS_SECTION = 'plans';
const CHECKLIST_SECTION = 'checklist';

/**
 * Left panel of the per-client care-plan view: back navigation, client identity,
 * and two independently-collapsible sections (weekly-planner style) — the plan
 * list and the selected plan's activation checklist. Both can be open at once; the
 * plan list opens by default. Selecting a different plan collapses the checklist
 * so it re-opens fresh with that plan's progress (its header shows the count).
 */
const CarePlanSidebar: React.FC<ComponentProps> = ({
  clientName,
  plans,
  selectedPlanId,
  selectedPlan,
  onSelectPlan,
  getPackageName,
  isLoading,
  onNewCarePlan,
}) => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<string[]>([PLANS_SECTION]);

  // Collapse the checklist whenever the selection changes (leaving the plan list
  // as-is), so its header count reflects the newly selected plan on next expand.
  useEffect(() => {
    setOpenSections((previous) => previous.filter((section) => section !== CHECKLIST_SECTION));
  }, [selectedPlanId]);

  const toggleSection = (id: string) =>
    setOpenSections((previous) =>
      previous.includes(id) ? previous.filter((section) => section !== id) : [...previous, id],
    );

  return (
    <div className="care-plan-sidebar">
      <Toolbar
        id="care-plan-sidebar"
        leftCluster={
          <ClickableIcon
            icon={faArrowLeft}
            id="care-plan-back-button"
            ariaLabel="Back to all clients"
            onClick={() => navigate('/clients/care-plans')}
            toolTip="Back to all clients"
          />
        }
        rightCluster={
          <Button size="sm" color="success" dataCy="care-plan-new-button" onClick={onNewCarePlan}>
            <Icon icon={faPlus} className="me-2" />
            New Care Plan
          </Button>
        }
      />
      <div className="identity">
        <div className="fw-bold" data-cy="care-plan-client-name">
          {clientName}
        </div>
      </div>
      <Accordion open={openSections} toggle={toggleSection} className="care-plan-sidebar-accordion">
        <AccordionItem>
          <AccordionHeader targetId={PLANS_SECTION}>Care plans</AccordionHeader>
          <AccordionBody accordionId={PLANS_SECTION}>
            {isLoading && (
              <div className="care-plan-section-loading">
                <Spinner loading />
              </div>
            )}
            {!isLoading && plans.length === 0 && (
              <div className="empty-state" data-cy="care-plan-empty-state">
                <p className="text-muted">This client has no care plans.</p>
              </div>
            )}
            {!isLoading && plans.length > 0 && (
              <ul className="plan-list" data-cy="care-plan-list">
                {plans.map((plan) => (
                  <CarePlanListItem
                    key={plan.id}
                    plan={plan}
                    packageName={getPackageName(plan.package_id)}
                    isSelected={plan.id === selectedPlanId}
                    onSelect={onSelectPlan}
                  />
                ))}
              </ul>
            )}
          </AccordionBody>
        </AccordionItem>
        {/* The checklist guides activation, so it's only shown while the plan is a Draft. */}
        {selectedPlan && !selectedPlan.activated_at && (
          <ActivationChecklistPanel plan={selectedPlan} targetId={CHECKLIST_SECTION} />
        )}
      </Accordion>
    </div>
  );
};

export default CarePlanSidebar;
